import {
	getFirestore,
	collection,
	doc,
	updateDoc,
	onSnapshot,
	query,
	orderBy,
	serverTimestamp,
	Timestamp,
	getDoc,
	writeBatch,
	addDoc,
	setDoc,
	deleteDoc,
} from "firebase/firestore"
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage"
import { getApp } from "firebase/app"

const db = getFirestore(getApp())

export interface GroupMember {
	id: string
	name: string
	type: "driver" | "backoffice" | "bot"
}

export interface Group {
	id: string
	name: string
	description?: string
	hasBotActive: boolean
	botTyping: boolean
	memberCount: number
	memberIds: string[]
	members: GroupMember[]
	lastMessage?: string
	lastMessageSenderName?: string
	lastReadAt: Record<string, Date>
	notifPrefs: Record<string, string>
	status: string
	createdAt: Date
	updatedAt?: Date
}

export interface Message {
	id: string
	content: string
	senderId: string
	senderType: "driver" | "backoffice" | "bot"
	senderName: string
	conversationId: string
	attachmentUrl?: string
	attachmentType?: "image" | "file"
	createdAt: Date
}

function groupFromDoc(snapshot: any): Group {
	const d = snapshot.data()
	const rawReadAt = d.lastReadAt || {}
	const lastReadAt: Record<string, Date> = {}
	for (const [key, val] of Object.entries(rawReadAt)) {
		if (val instanceof Timestamp) lastReadAt[key] = (val as Timestamp).toDate()
	}
	return {
		id: snapshot.id,
		name: d.name || "",
		description: d.description,
		hasBotActive: d.hasBotActive || false,
		botTyping: d.botTyping || false,
		memberCount: d.memberCount || 0,
		memberIds: d.memberIds || [],
		members: (d.members || []) as GroupMember[],
		lastMessage: d.lastMessage,
		lastMessageSenderName: d.lastMessageSenderName,
		lastReadAt,
		notifPrefs: (d.notifPrefs || {}) as Record<string, string>,
		status: d.status || "active",
		createdAt:
			d.createdAt instanceof Timestamp ? d.createdAt.toDate() : new Date(),
		updatedAt:
			d.updatedAt instanceof Timestamp ? d.updatedAt.toDate() : undefined,
	}
}

function messageFromDoc(snapshot: any): Message {
	const d = snapshot.data()
	return {
		id: snapshot.id,
		content: d.content || "",
		senderId: d.senderId || "",
		senderType: d.senderType || "backoffice",
		senderName: d.senderName || "",
		conversationId: d.conversationId || "",
		attachmentUrl: d.attachmentUrl,
		attachmentType: d.attachmentType,
		createdAt:
			d.createdAt instanceof Timestamp ? d.createdAt.toDate() : new Date(),
	}
}

export function watchGroups(callback: (groups: Group[]) => void): () => void {
	const q = query(collection(db, "groups"), orderBy("updatedAt", "desc"))
	return onSnapshot(q, (snap) => {
		callback(snap.docs.map(groupFromDoc))
	})
}

export function watchMessages(
	groupId: string,
	callback: (messages: Message[]) => void
): () => void {
	const q = query(
		collection(db, "groups", groupId, "messages"),
		orderBy("createdAt", "asc")
	)
	return onSnapshot(q, (snap) => {
		callback(snap.docs.map(messageFromDoc))
	})
}

export async function createGroup(params: {
	name: string
	description?: string
	hasBotActive: boolean
	members: GroupMember[]
	createdById: string
	createdByName: string
}): Promise<string> {
	const { name, description, hasBotActive, members, createdById } = params

	const allMembers: GroupMember[] = [...members]
	if (hasBotActive) {
		allMembers.push({ id: "bot", name: "agentD", type: "bot" })
	}

	const memberIds = allMembers.map((m) => m.id)
	const now = serverTimestamp()

	const docRef = await addDoc(collection(db, "groups"), {
		name,
		description: description || "",
		hasBotActive,
		memberCount: allMembers.filter((m) => m.type !== "bot").length,
		memberIds,
		members: allMembers,
		lastMessage: null,
		lastMessageSenderName: null,
		lastReadAt: { [createdById]: now },
		status: "active",
		createdAt: now,
		updatedAt: now,
	})

	return docRef.id
}

export async function uploadGroupImage(
	groupId: string,
	userId: string,
	file: File
): Promise<string> {
	const storage = getStorage(getApp())
	const filename = `${Date.now()}.jpg`
	const storageRef = ref(
		storage,
		`groups/${groupId}/images/${userId}/${filename}`
	)
	const snapshot = await uploadBytes(storageRef, file, {
		contentType: "image/jpeg",
	})
	return getDownloadURL(snapshot.ref)
}

export async function uploadGroupFile(
	groupId: string,
	userId: string,
	file: File
): Promise<string> {
	const storage = getStorage(getApp())
	const filename = `${Date.now()}_${file.name}`
	const storageRef = ref(
		storage,
		`groups/${groupId}/files/${userId}/${filename}`
	)
	const snapshot = await uploadBytes(storageRef, file, {
		contentType: file.type || "application/octet-stream",
	})
	return getDownloadURL(snapshot.ref)
}

export async function sendMessage(params: {
	groupId: string
	content: string
	senderId: string
	senderType: "driver" | "backoffice" | "bot"
	senderName: string
	attachmentUrl?: string
	attachmentType?: string
}): Promise<void> {
	const {
		groupId,
		content,
		senderId,
		senderType,
		senderName,
		attachmentUrl,
		attachmentType,
	} = params
	const groupRef = doc(db, "groups", groupId)
	const batch = writeBatch(db)

	const msgRef = doc(collection(db, "groups", groupId, "messages"))
	batch.set(msgRef, {
		content,
		senderId,
		senderType,
		senderName,
		conversationId: groupId,
		attachmentUrl: attachmentUrl ?? null,
		attachmentType: attachmentType ?? null,
		createdAt: serverTimestamp(),
	})

	const preview =
		attachmentType === "image"
			? "📷 Foto"
			: attachmentType === "file"
				? `📎 ${content}`
				: content
	batch.update(groupRef, {
		lastMessage: preview,
		lastMessageSenderName: senderName,
		updatedAt: serverTimestamp(),
		[`lastReadAt.${senderId}`]: serverTimestamp(),
	})

	await batch.commit()
}

export async function updateGroup(
	groupId: string,
	params: {
		name: string
		description: string
		members: GroupMember[]
		hasBotActive?: boolean
	}
): Promise<void> {
	const { name, description, hasBotActive } = params

	let members = params.members.filter((m) => m.type !== "bot")
	if (hasBotActive) {
		members = [...members, { id: "bot", name: "agentD", type: "bot" as const }]
	}

	const memberIds = members.map((m) => m.id)
	await updateDoc(doc(db, "groups", groupId), {
		name,
		description,
		hasBotActive: hasBotActive ?? false,
		members,
		memberIds,
		memberCount: members.filter((m) => m.type !== "bot").length,
		updatedAt: serverTimestamp(),
	})
}

export async function markAsRead(
	groupId: string,
	userId: string
): Promise<void> {
	await updateDoc(doc(db, "groups", groupId), {
		[`lastReadAt.${userId}`]: serverTimestamp(),
	})
}

export async function getGroupMembers(groupId: string): Promise<GroupMember[]> {
	const snapshot = await getDoc(doc(db, "groups", groupId))
	if (!snapshot.exists()) return []
	const data = snapshot.data()
	return (data.members || []) as GroupMember[]
}

export async function updateNotifPref(
	groupId: string,
	userId: string,
	pref: "all" | "mentions" | "none"
): Promise<void> {
	await updateDoc(doc(db, "groups", groupId), {
		[`notifPrefs.${userId}`]: pref,
	})
}

export async function setTyping(
	groupId: string,
	userId: string,
	userName: string
): Promise<void> {
	await setDoc(doc(db, "groups", groupId, "typing", userId), {
		name: userName,
		updatedAt: serverTimestamp(),
	})
}

export async function clearTyping(
	groupId: string,
	userId: string
): Promise<void> {
	await deleteDoc(doc(db, "groups", groupId, "typing", userId))
}

export function watchTyping(
	groupId: string,
	excludeId: string,
	callback: (names: string[]) => void
): () => void {
	return onSnapshot(collection(db, "groups", groupId, "typing"), (snap) => {
		const names = snap.docs
			.filter((d) => d.id !== excludeId)
			.map((d) => d.data().name as string)
			.filter(Boolean)
		callback(names)
	})
}

export async function getNotifPref(
	groupId: string,
	userId: string
): Promise<"all" | "mentions" | "none"> {
	const snap = await getDoc(doc(db, "groups", groupId))
	if (!snap.exists()) return "all"
	const data = snap.data()
	return (data.notifPrefs?.[userId] ?? "all") as "all" | "mentions" | "none"
}

export async function setBotTyping(
	groupId: string,
	isTyping: boolean
): Promise<void> {
	await updateDoc(doc(db, "groups", groupId), { botTyping: isTyping })
}

export async function archiveGroup(groupId: string): Promise<void> {
	await updateDoc(doc(db, "groups", groupId), {
		status: "archived",
		updatedAt: serverTimestamp(),
	})
}
